import { useCallback, useEffect, useMemo, useState } from 'react';
import { equipeEnvoisApi, rendezVousApi } from '../../api/resources';
import { useEquipes } from '../../api/useEquipes';
import { Shell } from '../../components/layout/Shell.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import { nomComplet } from '../../lib/acces';
import { adresseAssemblee } from '../../lib/adresse';
import { dateHeureSuisse, dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { nomDuChantier } from '../../lib/nom-chantier';
import { collaborateursEnPoste } from '../../lib/personnel';
import { C } from '../../theme/palette';

/** Ce que le bureau peut envoyer, dans l'ordre de la maquette. */
const TYPES = [
  ['MESSAGE', 'Message'],
  ['RDV', 'Rendez-vous'],
  ['PLAN', 'Plan / fichier'],
  ['CHANTIER_DU_JOUR', 'Chantier du jour'],
  ['CONSIGNE', 'Consigne'],
  ['DOCUMENT', 'Document'],
];

/** Titre de la partie 2, selon ce qu'on envoie. */
const TITRES_PARTIE = {
  MESSAGE: 'Le message',
  RDV: 'Le rendez-vous',
  PLAN: 'Le plan',
  CHANTIER_DU_JOUR: 'Le chantier',
  CONSIGNE: 'La consigne',
  DOCUMENT: 'Le document',
};

const TYPES_RDV = [
  ['chantier', 'Rendez-vous de chantier'],
  ['client', 'Rendez-vous avec un client'],
  ['reunion', 'Réunion au bureau'],
  ['autre', 'Autre'],
];

/** Un identifiant du serveur : les fiches pas encore enregistrées n'en ont pas. */
const idServeur = (id) => typeof id === 'number' && Number.isFinite(id);

/** Demain, à la date locale (`AAAA-MM-JJ`). */
function demain() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const deux = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + deux(d.getMonth() + 1) + '-' + deux(d.getDate());
}

/** L'adresse d'un chantier de l'interface (`adr` + `npa`). */
const adresseDuChantier = (c) => adresseAssemblee(c?.adr ?? c?.adresse, c?.npa);

/** Retire les champs vides : le serveur ne reçoit que ce qui est rempli. */
function sansVides(objet) {
  return Object.fromEntries(
    Object.entries(objet).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );
}

const court = (texte, max = 80) => {
  const t = String(texte || '').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
};

const formulaireVide = (type, estMagasin) => ({
  type,
  texte: '',
  titre: '',
  typeRdv: estMagasin ? 'client' : 'chantier',
  date: type === 'CHANTIER_DU_JOUR' ? demain() : '',
  heureDebut: '',
  heureFin: '',
  modeLieu: estMagasin ? 'autre' : 'chantier',
  siteId: '',
  lieuNom: '',
  lieuAdresse: '',
  avecQui: '',
  rendezVousId: '',
});

const styleTitrePartie = {
  fontSize: 12,
  fontWeight: 800,
  color: C.grey,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};

const styleLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: C.grey,
};

const styleChamp = { ..._inp, fontSize: 14, boxSizing: 'border-box' };

/** Un champ avec son libellé, comme la maquette. */
function Champ({ label, children }) {
  return (
    <label style={styleLabel}>
      {label}
      {children}
    </label>
  );
}

/** Une puce à cocher (destinataire, toute l'équipe). */
function Puce({ on, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      style={{
        padding: '9px 12px',
        borderRadius: 99,
        border: '1px solid ' + (on ? C.teal : C.border),
        background: on ? C.teal : C.white,
        color: on ? '#fff' : C.ink,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {on ? '✓ ' : ''}
      {children}
    </button>
  );
}

/** Deux choix côte à côte (chantier existant / autre lieu). */
function Bascule({ choix, valeur, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
      {choix.map(([id, libelle]) => {
        const on = valeur === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(id)}
            style={{
              padding: '10px 6px',
              borderRadius: 10,
              border: on ? '2px solid ' + C.teal : '1.5px solid ' + C.border,
              background: on ? C.tealL : C.white,
              color: on ? C.teal : C.ink,
              fontSize: 12.5,
              fontWeight: on ? 800 : 700,
              cursor: 'pointer',
            }}
          >
            {libelle}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Le bureau (patron, secrétaire ou technicien) envoie à un employé, à
 * plusieurs ou à toute l'équipe d'un chantier : un message, un rendez-vous,
 * un plan, le chantier du jour, une consigne ou un document. Leur cloche
 * s'allume, et la liste « Déjà envoyés » montre qui l'a vu et qui vient.
 */
export default function EnvoyerEquipe({
  onBack,
  employes = [],
  chantiers = [],
  initial = {},
  estMagasin = false,
  onEnvoye,
  onNav,
  soc,
  estMixte,
  univers,
  setUnivers,
}) {
  const types = useMemo(
    () => TYPES.filter(([id]) => !(estMagasin && id === 'CHANTIER_DU_JOUR')),
    [estMagasin],
  );
  // Un chef d'équipe limité : le serveur ne lui donne que son équipe et ses
  // chantiers ; l'écran le lui dit.
  const { equipes, limite } = useEquipes();
  const typeDeDepart = types.some(([id]) => id === initial?.type) ? initial.type : 'MESSAGE';

  const personnes = useMemo(
    () => collaborateursEnPoste(employes).filter((e) => idServeur(e.id)),
    [employes],
  );
  const chantiersReels = useMemo(
    () =>
      (chantiers || []).filter(
        (c) => c && idServeur(c.id) && c.statut !== 'Terminé' && c.statut !== 'Annulé',
      ),
    [chantiers],
  );
  const choisisDeDepart = () =>
    (initial?.employeIds || []).filter((id) => personnes.some((e) => e.id === id));

  const [f, setF] = useState(() => formulaireVide(typeDeDepart, estMagasin));
  const [fichier, setFichier] = useState(null);
  const [cleFichier, setCleFichier] = useState(0);
  const [employeIds, setEmployeIds] = useState(choisisDeDepart);
  const [equipeDuChantierId, setEquipeDuChantierId] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [ok, setOk] = useState(false);

  const [envois, setEnvois] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreurListe, setErreurListe] = useState('');
  const [ouvert, setOuvert] = useState(null);

  // Plan lié à un rendez-vous : les rendez-vous à venir du chantier choisi.
  const [rdvs, setRdvs] = useState([]);
  useEffect(() => {
    if (f.type !== 'PLAN' || typeof rendezVousApi?.liste !== 'function') return undefined;
    let vivant = true;
    Promise.resolve()
      .then(() => rendezVousApi.liste())
      .then((l) => vivant && setRdvs(Array.isArray(l) ? l : []))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [f.type]);

  const charge = useCallback(async () => {
    try {
      const liste = await equipeEnvoisApi.liste();
      setEnvois(Array.isArray(liste) ? liste : []);
      setErreurListe('');
    } catch (e) {
      setErreurListe(e?.message || tr("La liste n'a pas pu être chargée."));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charge();
  }, [charge]);

  const maj = (champs) => {
    setOk(false);
    setErreur('');
    setF((p) => ({ ...p, ...champs }));
  };

  const choisitType = (type) => {
    setOk(false);
    setErreur('');
    setF((p) => ({
      ...p,
      type,
      date: type === 'CHANTIER_DU_JOUR' && !p.date ? demain() : p.date,
    }));
  };

  const choisitFichier = (e) => {
    const fi = e.target.files?.[0] || null;
    setOk(false);
    setErreur('');
    setFichier(fi);
    // Le titre du document reprend le nom du fichier, s'il est encore vide.
    if (fi && f.type === 'DOCUMENT' && !f.titre.trim()) {
      setF((p) => ({ ...p, titre: fi.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const basculeEmploye = (id) => {
    setOk(false);
    setEmployeIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };
  const toutLeMonde = personnes.length > 0 && personnes.every((e) => employeIds.includes(e.id));
  const basculeTous = () => {
    setOk(false);
    setEmployeIds(toutLeMonde ? [] : personnes.map((e) => e.id));
  };

  const avecChantiers = !estMagasin;
  const surChantier = avecChantiers && f.modeLieu === 'chantier';
  const chantierChoisi = chantiersReels.find((c) => String(c.id) === String(f.siteId));

  const lieuRempli = surChantier ? !!f.siteId : !!(f.lieuNom.trim() || f.lieuAdresse.trim());
  const contenuRempli = (() => {
    switch (f.type) {
      case 'MESSAGE':
      case 'CONSIGNE':
        return !!f.texte.trim();
      case 'RDV':
        return !!f.date && !!f.heureDebut && lieuRempli;
      case 'PLAN':
      case 'DOCUMENT':
        return !!fichier;
      case 'CHANTIER_DU_JOUR':
        return !!f.siteId && !!f.date;
      default:
        return false;
    }
  })();
  const aQui = employeIds.length > 0 || !!equipeDuChantierId;
  const pret = contenuRempli && aQui && !envoi;

  const lieu = () =>
    surChantier
      ? { siteId: f.siteId ? Number(f.siteId) : undefined }
      : { lieuNom: f.lieuNom.trim(), lieuAdresse: f.lieuAdresse.trim() };

  const demande = () => {
    const base = {
      type: f.type,
      employeIds,
      equipeDuChantierId: equipeDuChantierId ? Number(equipeDuChantierId) : undefined,
    };
    switch (f.type) {
      case 'RDV':
        return sansVides({
          ...base,
          typeRdv: f.typeRdv,
          date: f.date,
          heureDebut: f.heureDebut,
          heureFin: f.heureFin,
          avecQui: f.avecQui.trim(),
          texte: f.texte.trim(),
          ...lieu(),
        });
      case 'PLAN':
        return sansVides({
          ...base,
          titre: f.titre.trim(),
          texte: f.texte.trim(),
          ...lieu(),
          rendezVousId: surChantier && f.rendezVousId ? Number(f.rendezVousId) : undefined,
        });
      case 'DOCUMENT':
        return sansVides({ ...base, titre: f.titre.trim(), texte: f.texte.trim() });
      case 'CHANTIER_DU_JOUR':
        return sansVides({
          ...base,
          siteId: Number(f.siteId),
          date: f.date,
          texte: f.texte.trim(),
        });
      default:
        return sansVides({ ...base, texte: f.texte.trim() });
    }
  };

  const envoie = async () => {
    if (!pret) return;
    setEnvoi(true);
    setErreur('');
    setOk(false);
    try {
      const d = demande();
      const resultat =
        f.type === 'PLAN' || f.type === 'DOCUMENT'
          ? await equipeEnvoisApi.envoieFichier(d, fichier, fichier.name)
          : await equipeEnvoisApi.envoie(d);
      setOk(true);
      setF(formulaireVide(f.type, estMagasin));
      setFichier(null);
      setCleFichier((k) => k + 1);
      setEmployeIds(choisisDeDepart());
      setEquipeDuChantierId('');
      if (onEnvoye) onEnvoye(resultat);
      charge();
    } catch (e) {
      setErreur(e?.message || tr("L'envoi n'a pas abouti."));
    } finally {
      setEnvoi(false);
    }
  };

  const libelleBouton = envoi
    ? tr('Envoi…')
    : equipeDuChantierId && employeIds.length === 0
      ? tr("Envoyer à l'équipe du chantier")
      : trf('Envoyer à {0} personne(s)', employeIds.length);

  const selectChantier = (label, vide) => (
    <Champ label={label}>
      <select value={f.siteId} onChange={(e) => maj({ siteId: e.target.value })} style={styleChamp}>
        <option value="">{vide}</option>
        {chantiersReels.map((c) => (
          <option key={c.id} value={c.id}>
            {nomDuChantier(c, chantiersReels)}
          </option>
        ))}
      </select>
    </Champ>
  );

  const champLieu = () => (
    <>
      {avecChantiers && (
        <Bascule
          choix={[
            ['chantier', tr('Chantier existant')],
            ['autre', tr('Autre lieu (tapé à la main)')],
          ]}
          valeur={f.modeLieu}
          onChange={(modeLieu) => maj({ modeLieu })}
        />
      )}
      {surChantier ? (
        <>
          {selectChantier(tr("Chantier (l'adresse se remplit)"), tr('— Choisir le chantier —'))}
          {chantierChoisi && adresseDuChantier(chantierChoisi) && (
            <div style={{ fontSize: 12.5, color: C.grey, marginTop: -4 }}>
              📍 {adresseDuChantier(chantierChoisi)}
            </div>
          )}
        </>
      ) : (
        <>
          <Champ label={tr('Nom du lieu')}>
            <input
              value={f.lieuNom}
              onChange={(e) => maj({ lieuNom: e.target.value })}
              style={styleChamp}
            />
          </Champ>
          <Champ label={tr('Adresse')}>
            <input
              value={f.lieuAdresse}
              onChange={(e) => maj({ lieuAdresse: e.target.value })}
              style={styleChamp}
            />
          </Champ>
        </>
      )}
    </>
  );

  const champTexte = (label) => (
    <Champ label={label}>
      <textarea
        value={f.texte}
        onChange={(e) => maj({ texte: e.target.value })}
        rows={3}
        style={{ ...styleChamp, minHeight: 52, resize: 'vertical', fontFamily: 'inherit' }}
      />
    </Champ>
  );

  const champFichier = (label) => (
    <Champ label={label}>
      <input
        key={cleFichier}
        type="file"
        accept="image/*,application/pdf"
        onChange={choisitFichier}
        style={{ ...styleChamp, padding: 9 }}
      />
    </Champ>
  );

  const champDate = (label = tr('Date')) => (
    <Champ label={label}>
      <input
        type="date"
        value={f.date}
        onChange={(e) => maj({ date: e.target.value })}
        style={styleChamp}
      />
    </Champ>
  );

  const contenu = () => {
    switch (f.type) {
      case 'RDV':
        return (
          <>
            <Champ label={tr('Type')}>
              <select
                value={f.typeRdv}
                onChange={(e) => maj({ typeRdv: e.target.value })}
                style={styleChamp}
              >
                {TYPES_RDV.map(([id, libelle]) => (
                  <option key={id} value={id}>
                    {tr(libelle)}
                  </option>
                ))}
              </select>
            </Champ>
            {champDate()}
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}
            >
              <Champ label={tr('Heure début')}>
                <input
                  type="time"
                  value={f.heureDebut}
                  onChange={(e) => maj({ heureDebut: e.target.value })}
                  style={styleChamp}
                />
              </Champ>
              <Champ label={tr('Heure fin')}>
                <input
                  type="time"
                  value={f.heureFin}
                  onChange={(e) => maj({ heureFin: e.target.value })}
                  style={styleChamp}
                />
              </Champ>
            </div>
            <div style={styleLabel}>{tr('Lieu')}</div>
            {champLieu()}
            <Champ label={tr('Avec qui (facultatif)')}>
              <input
                value={f.avecQui}
                onChange={(e) => maj({ avecQui: e.target.value })}
                style={styleChamp}
              />
            </Champ>
            {champTexte(tr("Note pour l'équipe"))}
          </>
        );
      case 'PLAN':
        return (
          <>
            {champFichier(tr('Plan ou photo (image ou PDF)'))}
            {(() => {
              const aujourdhui = new Date().toISOString().slice(0, 10);
              const duChantier = rdvs.filter(
                (r) =>
                  surChantier &&
                  f.siteId &&
                  String(r.siteId) === String(f.siteId) &&
                  String(r.dateRdv || '') >= aujourdhui,
              );
              return duChantier.length > 0 ? (
                <Champ label={tr('Lié au rendez-vous (facultatif)')}>
                  <select
                    value={f.rendezVousId || ''}
                    onChange={(e) => maj({ rendezVousId: e.target.value })}
                    style={styleChamp}
                  >
                    <option value="">{tr('— Aucun —')}</option>
                    {duChantier.map((r) => (
                      <option key={r.id} value={r.id}>
                        {[dateSuisse(r.dateRdv), r.heure, r.titre].filter(Boolean).join(' · ')}
                      </option>
                    ))}
                  </select>
                </Champ>
              ) : null;
            })()}
            <Champ label={tr('Nom (facultatif)')}>
              <input
                value={f.titre}
                onChange={(e) => maj({ titre: e.target.value })}
                style={styleChamp}
              />
            </Champ>
            <div style={styleLabel}>{tr('Lieu')}</div>
            {champLieu()}
            {champTexte(tr('Note (facultatif)'))}
          </>
        );
      case 'CHANTIER_DU_JOUR':
        return (
          <>
            {selectChantier(tr('Chantier'), tr('— Choisir le chantier —'))}
            {chantierChoisi && adresseDuChantier(chantierChoisi) && (
              <div style={{ fontSize: 12.5, color: C.grey, marginTop: -4 }}>
                📍 {adresseDuChantier(chantierChoisi)}
              </div>
            )}
            {champDate()}
            {champTexte(tr('Note (facultatif)'))}
          </>
        );
      case 'DOCUMENT':
        return (
          <>
            {champFichier(tr('Fichier (image ou PDF)'))}
            <Champ label={tr('Titre')}>
              <input
                value={f.titre}
                onChange={(e) => maj({ titre: e.target.value })}
                style={styleChamp}
              />
            </Champ>
            {champTexte(tr('Note (facultatif)'))}
          </>
        );
      case 'CONSIGNE':
        return champTexte(tr('Consigne'));
      default:
        return champTexte(tr('Message'));
    }
  };

  const libelleType = (type) => tr((TYPES.find(([id]) => id === type) || [type, type])[1]);
  const nomChantier = (id) => {
    const c = (chantiers || []).find((x) => x?.id === id);
    return c ? nomDuChantier(c, chantiers) : '';
  };

  return (
    <Shell
      active="equipe"
      onNav={onNav}
      soc={soc}
      back={!!onBack}
      onBack={onBack}
      pageTitle={limite ? tr('📨 Envoyer à mon équipe') : tr("📨 Envoyer à l'équipe")}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, color: C.ink }}>
        <div style={styleTitrePartie}>1 · {tr('Quoi')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
          {types.map(([id, libelle]) => {
            const on = f.type === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={on}
                onClick={() => choisitType(id)}
                style={{
                  padding: '12px 4px',
                  borderRadius: 12,
                  border: on ? '2px solid ' + C.teal : '1.5px solid ' + C.border,
                  background: on ? C.tealL : C.white,
                  color: on ? C.teal : C.ink,
                  fontSize: 12.5,
                  fontWeight: on ? 800 : 700,
                  cursor: 'pointer',
                }}
              >
                {tr(libelle)}
              </button>
            );
          })}
        </div>

        <div style={styleTitrePartie}>2 · {tr(TITRES_PARTIE[f.type])}</div>
        <Card
          style={{
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            borderRadius: 14,
          }}
        >
          {contenu()}
        </Card>

        <div style={styleTitrePartie}>3 · {limite ? tr('À qui — mon équipe') : tr('À qui')}</div>
        {personnes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.grey }}>{tr('Aucun employé actif.')}</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {personnes.map((e) => (
              <Puce key={e.id} on={employeIds.includes(e.id)} onClick={() => basculeEmploye(e.id)}>
                {nomComplet(e) || e.nom}
              </Puce>
            ))}
            <Puce on={toutLeMonde} onClick={basculeTous}>
              {limite ? tr('Toute mon équipe') : tr("Toute l'équipe")}
            </Puce>
          </div>
        )}
        {limite && (
          <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.45, marginTop: -4 }}>
            {trf(
              '{0} : seuls tes employés et tes chantiers apparaissent ici.',
              equipes.map((q) => q.nom).join(', '),
            )}
          </div>
        )}
        {avecChantiers && (
          <>
            <select
              aria-label={tr('Équipe du chantier…')}
              value={equipeDuChantierId}
              onChange={(e) => {
                setOk(false);
                setEquipeDuChantierId(e.target.value);
              }}
              style={styleChamp}
            >
              <option value="">{tr('Équipe du chantier…')}</option>
              {chantiersReels.map((c) => (
                <option key={c.id} value={c.id}>
                  {nomDuChantier(c, chantiersReels)}
                </option>
              ))}
            </select>
            {equipeDuChantierId && (
              <div style={{ fontSize: 12, color: C.grey, marginTop: -4 }}>
                {tr("Tous ceux prévus sur ce chantier à partir d'aujourd'hui.")}
              </div>
            )}
          </>
        )}

        {erreur && (
          <div
            role="alert"
            style={{
              background: C.redL,
              color: C.red,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {erreur}
          </div>
        )}
        {ok && (
          <div
            role="status"
            style={{
              background: C.greenL,
              color: C.green,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {tr("Envoyé. Leur cloche s'allume tout de suite.")}
          </div>
        )}
        <button
          type="button"
          disabled={!pret}
          onClick={envoie}
          style={{
            border: 'none',
            background: pret ? C.green : '#94A3B8',
            color: '#fff',
            borderRadius: 14,
            padding: 15,
            fontSize: 15,
            fontWeight: 800,
            cursor: pret ? 'pointer' : 'not-allowed',
          }}
        >
          {libelleBouton}
        </button>
        <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.45 }}>
          {tr(
            "Leur cloche s'allume tout de suite. Ici tu vois ensuite qui l'a vu et qui ne peut pas venir.",
          )}
        </div>

        <div style={{ ...styleTitrePartie, marginTop: 8 }}>{tr('Déjà envoyés')}</div>
        {erreurListe && (
          <div role="alert" style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>
            {erreurListe}
          </div>
        )}
        {chargement ? (
          <div style={{ fontSize: 12.5, color: C.grey }}>{tr('Chargement…')}</div>
        ) : (
          envois.length === 0 &&
          !erreurListe && (
            <div style={{ fontSize: 12.5, color: C.grey }}>{tr('Rien envoyé pour le moment.')}</div>
          )
        )}
        {envois.map((x) => {
          const estRdv = x.type === 'RDV';
          const deplie = ouvert === x.id;
          const quand = x.date
            ? dateSuisse(x.date) +
              (x.heureDebut ? ' · ' + x.heureDebut + (x.heureFin ? '–' + x.heureFin : '') : '')
            : '';
          const ou = x.siteId ? nomChantier(x.siteId) : adresseAssemblee(x.lieuNom, x.lieuAdresse);
          return (
            <Card key={x.id} style={{ borderRadius: 14 }}>
              <button
                type="button"
                aria-expanded={deplie}
                onClick={() => setOuvert(deplie ? null : x.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: 12,
                  cursor: 'pointer',
                  color: C.ink,
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      background: C.navy,
                      color: '#fff',
                      borderRadius: 99,
                      padding: '2px 8px',
                    }}
                  >
                    {libelleType(x.type)}
                  </span>
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>
                    {x.titre || court(x.texte)}
                  </span>
                </div>
                {(quand || ou) && (
                  <div style={{ fontSize: 12, color: C.grey, marginTop: 4 }}>
                    {quand && '📅 ' + quand}
                    {quand && ou && ' · '}
                    {ou && '📍 ' + ou}
                  </div>
                )}
                <div style={{ fontSize: 12, color: C.grey, marginTop: 4 }}>
                  {x.envoyeLe && trf('Envoyé le {0}', dateHeureSuisse(x.envoyeLe))}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 4, color: C.teal }}>
                  {trf('👁 {0}/{1} vus', x.vus ?? 0, x.total ?? 0)}
                  {estRdv && ' · ' + trf('✓ {0} · ✗ {1}', x.presents ?? 0, x.absents ?? 0)}
                </div>
              </button>
              {x.nomFichier && (
                <div style={{ padding: '0 12px 10px' }}>
                  <a
                    href={equipeEnvoisApi.adresseFichier(x.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12.5, fontWeight: 700, color: C.teal }}
                  >
                    📎 {x.nomFichier}
                  </a>
                </div>
              )}
              {deplie && (
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: '4px 12px 12px',
                    borderTop: '1px solid ' + C.border,
                  }}
                >
                  {(x.destinataires || []).map((d) => (
                    <li
                      key={d.employeId}
                      style={{ padding: '8px 0', borderBottom: '1px solid ' + C.border }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{d.nom}</div>
                      <div style={{ fontSize: 12, color: d.vuLe ? C.green : C.grey }}>
                        {d.vuLe ? trf('Vu le {0}', dateHeureSuisse(d.vuLe)) : tr('Pas encore vu')}
                      </div>
                      {estRdv && d.reponse === 'PRESENT' && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.green }}>
                          {tr('✓ Sera là')}
                        </div>
                      )}
                      {estRdv && d.reponse === 'ABSENT' && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.red }}>
                          {d.reponseTexte
                            ? trf('✗ Ne peut pas : {0}', d.reponseTexte)
                            : tr('✗ Ne peut pas')}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </Shell>
  );
}
